import React from "react";
import { Link as pageLink } from "gatsby";
import parse from "html-react-parser";
import { Flex, Text, Box, TextLink } from "@indeed/ifl-components";
import { TextProps } from "@indeed/ifl-components";
import { ArticleHeroCreditStyles } from "styles/common";
import type { SystemStyleObject } from "@indeed/ifl-css";

export const Title: React.FC<{
  link: string;
  newTab: boolean;
  level?: TextProps["level"];
  children?: React.ReactNode;
}> = ({ link, newTab, level, children }) =>
  link ? (
    <Text
      as={pageLink}
      level={level}
      to={`/article/${link}`}
      target={newTab ? "_blank" : null}
      sx={{
        color: "neutral.1000",
        m: 0,
        mb: 2,
        maxWidth: "30ch",
        textDecoration: "none",
        "&:hover, &:focus": {
          textDecoration: "underline",
        },
      }}
    >
      {children}
    </Text>
  ) : (
    <Text
      level={level}
      as="h1"
      sx={{
        color: "neutral.1000",
        m: 0,
        mb: 2,
        maxWidth: "30ch",
        textDecoration: "none",
      }}
    >
      {children}
    </Text>
  );

export const SubTitle: React.FC<{
  level?: TextProps["level"];
  sx?: SystemStyleObject;
  children?: React.ReactNode;
}> = ({ level, children, sx }) => {
  return (
    <Text level={level} sx={sx}>
      {children}
    </Text>
  );
};

export const Avatar: React.FC<{
  avatar: string;
  avatarAltText: string;
  sx?: SystemStyleObject;
  props?: React.PropsWithChildren;
}> = ({ avatar, avatarAltText, sx, ...props }) => {
  return (
    <Box
      as="img"
      src={avatar}
      alt={avatarAltText}
      sx={{ height: "48px", width: "48px", borderRadius: "100%", mr: 3, ...sx }}
      {...props}
    />
  );
};

export const Author: React.FC<{
  link?: string;
  name: string;
  title?: string;
  placeOfWork?: string;
  sx?: SystemStyleObject;
  props?: React.PropsWithChildren;
}> = ({ link, name, title, placeOfWork, sx, ...props }) => {
  return (
    <Box>
      {link ? (
        <TextLink
          href={link}
          target="_blank"
          level="articleauthor"
          {...props}
          sx={{
            border: "none",
            textDecoration: "none",
            "&:hover": { textDecoration: "underline" },
            ...ArticleHeroCreditStyles,
            ...sx,
          }}
        >
          {name}
        </TextLink>
      ) : (
        <Text
          level="articleauthor"
          sx={{ ...ArticleHeroCreditStyles, ...sx }}
          {...props}
        >
          {name}
        </Text>
      )}
      {title ? (
        <Text sx={{ display: "block" }}>
          {`${title} ${placeOfWork ? `at ${placeOfWork}` : ""}`}
        </Text>
      ) : null}
    </Box>
  );
};

export const AuthorGroup: React.FC<{
  as?: React.ElementType<any>;
  children?: React.ReactNode;
  sx?: SystemStyleObject;
  props?: React.PropsWithChildren;
}> = ({ as, children, sx, props }) => {
  return (
    <Flex
      as={as || "figure"}
      sx={{
        alignItems: "flex-start",
        flexDirection: "column",
        width: "100%",
        ...sx,
      }}
      {...props}
    >
      {children}
    </Flex>
  );
};

//TODO: Remove ArticleContent component as it is not being used anywhere else
interface ArticleContentProps {
  title: string;
  description: string;
  author: string;
  authorTitle: string;
  authorLink: string;
  avatar: string;
  avatarAltText: string;
  link: string;
  highlight: string;
  newTab: boolean;
  sx?: SystemStyleObject;
  props?: React.PropsWithChildren;
}

const ArticleContent: React.FC<ArticleContentProps> = ({
  title,
  description,
  author,
  authorTitle,
  authorLink,
  avatar,
  avatarAltText,
  link,
  highlight,
  newTab = true,
  sx,
  ...props
}) => {
  const articleTitle = highlight ? "articletitlehighlight" : "articletitle";
  const articleContent = highlight
    ? "articlecontenthighlight"
    : "articlecontent";

  return (
    <Box as="figcaption" {...props} sx={{ ...sx }}>
      <Title level={articleTitle} link={link} newTab={newTab}>
        {parse(title)}
      </Title>
      {description ? (
        <SubTitle
          level={articleContent}
          sx={{
            maxWidth: "45ch",
            "> p": {
              m: 0,
              mt: 2,
              mb: [4, null, null, 7],
            },
          }}
        >
          {parse(description)}
        </SubTitle>
      ) : null}

      {avatar || author ? (
        <AuthorGroup>
          <Avatar avatar={avatar} avatarAltText={avatarAltText} />
          {avatar ? (
            <Box as="figcaption" sx={sx} {...props}>
              <Author name={author} link={authorLink} />
            </Box>
          ) : (
            <Author name={author} link={authorLink} />
          )}
        </AuthorGroup>
      ) : null}
    </Box>
  );
};

export default ArticleContent;
